import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useLockedBody } from 'usehooks-ts';

import { CHANNEL } from '@boss/constants/corporate-jobs';
import { useIsResponsive, useRouter } from '@boss/hooks';
import { LocalizedSlugs, Navigation } from '@boss/types/corporate-jobs';
import { CorporateMainNav } from '@boss/ui';

type Props = {
  navigation: Navigation;
  localizedSlugs: LocalizedSlugs;
  type?: 'default' | 'simple';
  mobileMenuClassName?: string;
};

const Header = ({ navigation, localizedSlugs, type = 'default', mobileMenuClassName }: Props) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const { locale, asPath } = useRouter();
  const isMobile = useIsResponsive();
  const { highlightedCta, headerNavigationItems, headerLogo, secondaryCtas, footerLogo } = navigation;

  useLockedBody(isOpen, 'root');

  const handleMobileMenuClick = () => {
    setIsOpen(isOpen => !isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isMobile) {
      closeMenu();
    }
  }, [isMobile]);

  useEffect(() => closeMenu(), [asPath, locale]);

  // NOTE: Added safeguard to let ts play along
  if (!locale) {
    return null;
  }

  const highlightedButtonType = () => {
    switch (CHANNEL) {
      case 'corporate':
        return 'secondary';
      case 'jobs-b2c':
        return 'tertiary';
      case 'jobs-b2b':
        return 'primary';
    }
  };

  const activeLinkStyle = CHANNEL === 'jobs-b2c' ? 'font-bold' : 'text-green-sheen';
  const linkStyle = `hover:${activeLinkStyle}`;

  return (
    <CorporateMainNav
      activeLinkStyle={activeLinkStyle}
      className={twMerge('z-nav', isOpen && 'fixed w-full')}
      handleMobileMenuClick={handleMobileMenuClick}
      highlightedButtonClassName={CHANNEL === 'jobs-b2c' ? 'capitalize' : 'uppercase'}
      highlightedButtonMobileType="mobileMenu"
      highlightedButtonType={highlightedButtonType()}
      highlightedLinks={highlightedCta}
      invertedLogo={footerLogo}
      isOpen={isOpen}
      linkStyle={linkStyle}
      links={headerNavigationItems}
      localizedSlugs={localizedSlugs}
      logo={headerLogo}
      mobileMenuClassName={mobileMenuClassName}
      topLinks={secondaryCtas}
      translations={{
        menuLabelClosed: t('header.closed.label'),
        menuLabelOpen: t('header.open.label'),
      }}
    />
  );
};

export default Header;
